/*
 * Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from "react";
import { FormattedMessage } from "react-intl";
import useStyles from "./styles";
import dayjs from "dayjs";
import clsx from "clsx";
import { Box } from "@material-ui/core";

export const DEFAULT_ZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone?.toString() || "UTC";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function formatWithTimeZone(
  date: Date,
  timeZone: string,
  formatString = "HH:mm:ss"
) {
  return dayjs(date).format(formatString);
}
const parseDate = (date: string | Date) => {
  if (typeof date === "string") {
    return date === "" ? new Date() : new Date(date);
  }
  return date;
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const formatLogTimeStamp = (
  date: Date,
  timeZone: string = DEFAULT_ZONE
) => {
  const parsedDate = parseDate(date);
  return `${formatWithTimeZone(
    parsedDate,
    timeZone,
    "YYYY-MM-DDTHH:mm:ss.SSSZ"
  )}`;
};

export const LOG_LEVEL_TRACE = "TRACE";
export const LOG_LEVEL_DEBUG = "DEBUG";
export const LOG_LEVEL_INFO = "INFO";
export const LOG_LEVEL_WARN = "WARN";
export const LOG_LEVEL_ERROR = "ERROR";

export type LogLevel =
  | typeof LOG_LEVEL_TRACE
  | typeof LOG_LEVEL_DEBUG
  | typeof LOG_LEVEL_INFO
  | typeof LOG_LEVEL_WARN
  | typeof LOG_LEVEL_ERROR;

export interface CollectedLogLineWithKey {
  timestamp: string;
  level?: LogLevel;
  displayTimeStamp?: string;
  message?: string;
  randomKey: number;
  isNewLog: boolean;
}

interface LogLineProps {
  getColor: (name: string) => string;
  logEntry: CollectedLogLineWithKey;
  timeZone: string;
  isExpanded?: boolean;
  arrowElementProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  parentElementProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
}

const messageText = (
  <FormattedMessage
    id="components.InfiniteLogsPanel.LogLine.message"
    defaultMessage="Message"
  />
);

export default function LogLine(props: LogLineProps) {
  const { logEntry, timeZone, getColor, parentElementProps } = props;
  const classes = useStyles();

  const getLogColorCode = (levelString: LogLevel | string) => {
    if (levelString.toUpperCase() === LOG_LEVEL_INFO) {
      return classes.infoLogs;
    }
    return classes.errorLogs;
  };

  // Note: Please avoid using MUI element in this component
  return (
    <div>
      <div key={logEntry.randomKey} data-testid="log-panel-entry">
        <div
          className={classes.logLineWrapper}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...parentElementProps}
        >
          <span className={classes.infoSection}>
            <span
              className={clsx(
                getLogColorCode(LOG_LEVEL_INFO),
                classes.toolTipParent
              )}
            >
              {formatLogTimeStamp(
                new Date(logEntry.displayTimeStamp || logEntry.timestamp),
                timeZone
              )}
              <Box marginRight={4} />
            </span>
            {logEntry?.message && (
              <span
                className={clsx(
                  logEntry?.message?.toUpperCase().includes(LOG_LEVEL_ERROR)
                    ? classes.errorLogs
                    : getColor(logEntry?.message),
                  classes.toolTipParent
                )}
              >
                {logEntry?.message}
                <Box marginRight={4} />
                <span className={classes.toolTipChild}>{messageText}</span>
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
